/**
 * The turn — which nobody takes any more.
 *
 * A campaign turn is a small state machine rather than one function, for one
 * reason: a battle can suspend it. When the player's army walks into somebody
 * else's province, the turn stops in the middle of `Battles`, hands a
 * `PendingBattle` to the outside world, and does nothing further until a result
 * comes back — at which point `advance` picks up exactly where it left off and
 * finishes the turn. Every intermediate position is stored in `phase`, which is
 * hashed and saved, so a campaign can be saved with a battle half-fought and
 * will resume in the same place.
 *
 * ── What changed when the turns went away ────────────────────────────────
 *
 * The machine is untouched. What changed is who winds it. There is no longer an
 * "end turn" for a player to press: `stepRealm` runs every tick, watches the
 * Long Ledger, and starts a resolution the moment the month turns — one phase
 * per tick, so the work is done on a schedule rather than in a lump. A turn is
 * therefore an *interval*: `MONTH_SECONDS` of simulated time, 280 seconds at
 * normal speed, and every number in `types.ts` that was written "per turn"
 * (upkeep, regrowth, unrest decay, siege duration, camp growth) now reads "per
 * month" without a single one of them changing value.
 *
 * `endTurn` survives as the way to resolve one *immediately*. Nothing in the
 * game reaches it any more; the balance harness and the tests do, because
 * running two hundred months of realm in a test should not take fifteen hours.
 *
 * Order within a turn: catch what is on the roads, march, fight, besiege,
 * collect, and only then does the council of AI nations decide what to do about
 * any of it. The AI reacting to the *result* of the turn rather than to its
 * opening position is what makes it look like it is paying attention.
 *
 * `Roads` runs before `March` and it has to. Two hosts crossing the same link in
 * opposite directions are only in the same place *while* they are marching; move
 * them first and they have swapped provinces without ever seeing each other,
 * which is the oldest bug in strategic games and reads as the map cheating.
 *
 * The player's warning list is rebuilt at the very end of the turn, after the
 * council has issued its orders. That placement is the whole of the fairness
 * guarantee: an order given in council is on the player's map before the turn in
 * which it is carried out, so nothing can arrive without having been announced.
 */

import { RESOURCE_KEYS, type ResourceKey } from '../types.ts';
import { MONTHS_PER_YEAR, MONTH_NAMES, monthsElapsed } from '../systems/calendar.ts';
import type { World } from '../world.ts';
import { armiesIn, armiesStandingIn, applyLosses, continueMarch, rawPoints, removeArmy } from './armies.ts';
import {
  autoResolveBattle,
  captureProvince,
  openBattle,
  openCampBattle,
  openRoadBattle,
} from './conquest.ts';
import { campAt, hasCamp, stepBandits } from './bandits.ts';
import { shelterDefender, standingGarrison } from './garrisons.ts';
import {
  depositGoods,
  holdingIncome,
  stepHoldings,
  supplyMap,
  tierRegrowth,
  tierUnrestRelief,
} from './holdings.ts';
import {
  advanceVoyages,
  fleetUpkeep,
  laneAdjacency,
  nextSeaContact,
  portIncome,
  reconcileFleets,
  resolveSeaContact,
} from './fleets.ts';
import { eatFood } from './food.ts';
import { landArmy, logRoadContact, nextRoadContact } from './marches.ts';
import { isTrespassing } from './diplopass.ts';
import { refreshWarnings } from './warnings.ts';
import {
  atWar,
  checkElimination,
  garrisonCap,
  logLine,
  mayAttack,
  nationName,
  provinceName,
} from './state.ts';
import { councilTurn } from './ai.ts';
import {
  estateGarrisonScale,
  estateGoldScale,
  estateLevyScale,
  estateTurn,
  estateUnrestScale,
} from './estates.ts';
import { answerAsCouncil, realmEventTurn } from './chronicleturn.ts';
import { diplomaticTurn } from './diploturn.ts';
import { tradeTurn } from './tradeturn.ts';
import { marketTurn } from './marketai.ts';
import { tradeIncomeOf } from './trade.ts';
import {
  ArmyStance,
  GARRISON_REGROWTH,
  SHIP_UPKEEP_GOLD,
  SIEGE_TURNS_PER_FORT,
  UNREST_DECAY,
  UNREST_CORE,
  UNREST_STRAIN,
  UPKEEP_GOLD_PER_POINT,
  TurnPhase,
  type Army,
  type CampaignState,
} from './types.ts';

/**
 * Resolve a month now, without waiting for the ledger.
 *
 * The old "end turn" button. Nothing in the running game calls it — the clock
 * does the winding — but `tools/campaign-sweep.ts` and the campaign tests do,
 * because a hundred months of realm is nine hours of simulated time and a test
 * is not going to sit through it. Resolving early does not *gain* a month: the
 * realm waits until the ledger catches up before it sits again.
 */
export function endTurn(w: World): void {
  const c = w.campaign;
  if (!c || c.over) return;
  if (c.phase !== TurnPhase.Orders) return;
  if (c.pending) return;
  // A question the realm has put to the player stops this exactly as a battle
  // does, and it has to stop it *here* as well as in `stepRealm`: the harness and
  // the tests reach the month through this door, and a harness that could resolve
  // straight past an unanswered question would be measuring a realm the player
  // could never actually play.
  if (c.pendingEvent) return;
  c.phase = TurnPhase.Roads;
  advance(w);
}

/**
 * A whole month, headlessly — and the door every harness should be using.
 *
 * `endTurn` resolves a month *unless* something needs a human. Two things do: a
 * battle the player would fight by hand, and a question the realm has put to
 * them. Both suspend the resolution on purpose, and a driver that does not
 * answer them simply stops.
 *
 * ── This was already broken, and badly ───────────────────────────────────
 *
 * `campaign-sweep.ts` called `endTurn` in a bare loop and nothing else. Measured
 * on the unmodified build: **the realm froze on turn 4, 4, 6, 10 and 26** across
 * five seeds, the moment anybody attacked the idle player seat and `c.pending`
 * came back with `playerFights`. Every number the sweep has ever printed — LEAD,
 * PRESS, churn, camps, and the 0.41 ms a turn — was measured on a realm stopped
 * in its first two years, and the per-turn cost was the cost of an early return.
 * `tests/campaign-balance.test.ts` drove `endTurn` the same way.
 *
 * So: one door, here, next to `endTurn`, rather than the same four lines copied
 * into six harnesses and forgotten in two of them. The battle is settled
 * abstractly — which is exactly what an AI nation would have got — and the
 * question is answered by `answerAsCouncil`, which runs the *same* weighting the
 * other twenty-nine crowns use. An idle seat therefore plays like a crown rather
 * than like somebody who always clicks the first button, which is the only
 * defensible default for an instrument that is measuring the AI.
 *
 * Nothing in the running game calls this. The clock winds the realm and the
 * player answers their own questions.
 */
export function endTurnHeadless(w: World): void {
  const c = w.campaign;
  if (!c || c.over) return;
  if (c.pendingEvent) answerAsCouncil(c, w.rngCampaign);
  endTurn(w);
  let guard = 0;
  while (guard++ < 96) {
    if (c.pending) {
      autoResolveBattle(w);
      advance(w);
      continue;
    }
    if (c.pendingEvent) {
      answerAsCouncil(c, w.rngCampaign);
      continue;
    }
    break;
  }
}

/**
 * The realm's clock: called every tick from `stepWorld`, on the same `dt` as
 * the settlement.
 *
 * Three states, in the order they are checked, and the order is the argument:
 *
 *   1. **A battle the player owes an answer for suspends everything.** `pending`
 *      is set the moment a fight the player may take by hand is found, and until
 *      a result comes back the realm does not move — the same suspension that
 *      always existed, now doing double duty as the guarantee that walking away
 *      to fight cannot cost you provinces you were not there to defend. The
 *      council is inside this: the AI nations get their orders from
 *      `TurnPhase.Council`, which is one of the phases that is not being run.
 *      A battle must not cost the player the world.
 *   2. **Mid-resolution, do one phase.** One phase per tick rather than the
 *      whole month in one lump — six ticks, a fifth of a second, so a march and
 *      the fight it walks into are visibly separate events rather than a
 *      simultaneous jolt. It also keeps the worst tick of a campaign cheap,
 *      which matters when the settlement is now ticking alongside it.
 *   3. **Otherwise, watch the month.** `monthsElapsed` is read off the Long
 *      Ledger; when it has moved past the turn counter, the council sits. The
 *      realm holds no clock of its own on purpose — one clock cannot drift from
 *      itself, and `calendarDay` is already hashed and saved.
 */
export function stepRealm(w: World): void {
  const c = w.campaign;
  if (!c || c.over) return;
  if (c.pending) return;
  /**
   * …and a question the realm has put to the player suspends it on exactly the
   * same terms, for exactly the same two reasons.
   *
   * It is *state* rather than a UI pause, so two replays that spent different
   * amounts of wall-clock time in front of the modal are still the same campaign.
   * And it is the guarantee that stopping to think cannot cost you provinces you
   * were not there to defend — a question must not cost the player the world any
   * more than a battle may.
   *
   * A question is only ever raised at the very end of a council, so this suspends
   * the *next* month rather than freezing this one halfway through. The player's
   * town is a separate `World` and keeps running: the crown is being asked
   * something, and the masons should not down tools over it.
   */
  if (c.pendingEvent) return;
  if (c.phase !== TurnPhase.Orders) {
    advanceOnce(w);
    return;
  }
  // `c.turn` counts from 1 and names the month being lived through, so turn 1 is
  // months-elapsed 0. A jump of several months — the console's `date` command
  // does that — is caught up one resolution at a time rather than skipped.
  if (c.turn <= monthsElapsed(w)) c.phase = TurnPhase.Roads;
}

/**
 * Drive the machine forward until it either wants orders again or is waiting on
 * a battle the player has to answer for.
 */
export function advance(w: World): void {
  const c = w.campaign;
  if (!c) return;
  let guard = 0;
  while (guard++ < 256) {
    if (c.pending) return;
    if (c.phase === TurnPhase.Orders) return;
    advanceOnce(w);
  }
}

/**
 * One step of the machine: exactly one phase's worth of work, or one contact.
 *
 * Split out of `advance` so the real-time driver can spend a tick on each. The
 * body is the old loop body, unchanged — the ordering argument at the top of
 * this file is about *this* switch and is as load-bearing as it ever was.
 */
function advanceOnce(w: World): void {
  const c = w.campaign;
  if (!c || c.pending) return;
  switch (c.phase) {
    case TurnPhase.Roads:
      if (!doNextRoadContact(w)) c.phase = TurnPhase.Seas;
      break;
    /**
     * The water, and it sits here for the same reason `Roads` sits first.
     *
     * Contacts before movement: two fleets crossing the same lane in opposite
     * directions are only in the same water *while* they are sailing, and a
     * phase that advanced the voyages first would let a war fleet and a troop
     * convoy pass straight through one another. Then every voyage advances a
     * month, so anything making landfall is ashore in time for `March` and
     * `Battles` to treat it exactly like a host that walked.
     *
     * Unlike the road contacts above, these do not come back one at a time.
     * They cannot suspend the turn: a battle at sea is never fought by hand, so
     * there is nothing for the player to answer and no reason to spend six
     * ticks doing what one can do.
     */
    case TurnPhase.Seas:
      doSeas(w);
      c.phase = TurnPhase.March;
      break;
    case TurnPhase.March:
      doMarch(c);
      c.phase = TurnPhase.Battles;
      break;
    case TurnPhase.Battles:
      if (!doNextContact(w)) c.phase = TurnPhase.Sieges;
      break;
    case TurnPhase.Sieges:
      doSieges(c);
      c.phase = TurnPhase.Income;
      break;
    case TurnPhase.Income:
      doIncome(c);
      // Trade, after the tithe. `doIncome` settles unrest and refills garrisons
      // before it prices anything, and trade is scaled by the same board — so it
      // has to read it after, or a province would be paying tax on one month's
      // unrest and trade on the previous one's. This is the one line the trade
      // system asks of this file; the order inside the month is argued in
      // `tradeturn.ts`.
      tradeTurn(w);
      stepBandits(c, w.rngCampaign);
      /**
       * The holdings, last: masons, charters, city industry, and the crowns
       * deciding what to build next.
       *
       * It takes no `rng` and that is load-bearing rather than incidental —
       * every crown's building council sits on a calendar precisely so that this
       * system draws nothing from the shared campaign stream and leaves every
       * bandit, battle and betrothal downstream of it exactly where it was. See
       * `developmentCouncil`.
       */
      stepHoldings(c);
      c.phase = TurnPhase.Council;
      break;
    case TurnPhase.Council:
      // Diplomacy first, and it has to be: a crown decides where to march on
      // the strength of who it is at war with, so the wars have to be settled
      // for the month before the hosts are given their orders. This is the one
      // line the diplomacy system asked of this file.
      diplomaticTurn(w);
      councilTurn(w);
      /**
       * The market, after the military council has spent. What a crown sells is
       * what this month's musters and hulls left over, and what it bids for is
       * what they ran short of — so the book always describes the board as the
       * council left it, not as it stood before the orders went out. One pass:
       * the AI posts, then the whole book is matched, deterministically, with
       * no random number anywhere in it. This is the one line the market asks
       * of this file; the order inside the sitting is argued in `marketai.ts`.
       */
      marketTurn(c);
      // The four interests inside every crown, before the turn counter moves —
      // they read the month that has just been resolved, which is the month they
      // are an opinion about. It draws no random number, so everything downstream
      // of it sits exactly where it did.
      estateTurn(c);
      c.turn++;
      c.phase = TurnPhase.Orders;
      logLine(c, `— ${monthName(c.turn)} —`);
      // Last, so it sees the orders the council has just given. This is where
      // the fairness rule is actually kept.
      refreshWarnings(c);
      /**
       * …and then the realm asks somebody something.
       *
       * Absolutely last, and the placement is the safety argument. By this point
       * the month is *completely* resolved — marched, fought, besieged, collected,
       * ordered, warned — so a question the player has to answer suspends the next
       * month rather than freezing this one halfway through, and there is no
       * half-finished phase for a save to have to round-trip.
       */
      realmEventTurn(w);
      return;
    default:
      c.phase = TurnPhase.Orders;
      return;
  }
  if (c.over) c.phase = TurnPhase.Orders;
}

/**
 * The chronicle's heading for a month, from the turn counter alone.
 *
 * Turn 1 is the first month of Year 1, so the arithmetic is the same the Long
 * Ledger does, one month behind — but taken off `c.turn` rather than off
 * `calendarDay`, so a harness that resolves months without ticking a clock still
 * gets headings that count up.
 */
function monthName(turn: number): string {
  const idx = Math.max(0, turn - 1);
  return `${MONTH_NAMES[idx % MONTHS_PER_YEAR]}, Year ${Math.floor(idx / MONTHS_PER_YEAR) + 1}`;
}

// ── The sea ────────────────────────────────────────────────────────────────

/**
 * Fight everything that meets on the water, then carry everybody a month
 * further on.
 *
 * The guard is the same one `advance` keeps, and it is needed for the same
 * reason: `resolveSeaContact` can sink a fleet and remove another, so the list
 * `nextSeaContact` walks changes underneath the loop. Bounded rather than
 * trusted, because a bug that made a contact irresolvable would otherwise hang
 * the realm rather than merely misreport it.
 */
function doSeas(w: World): void {
  const c = w.campaign;
  if (!c) return;
  let guard = 0;
  while (guard++ < 64) {
    const contact = nextSeaContact(c);
    if (!contact) break;
    resolveSeaContact(c, w.rngCampaign, contact);
  }
  advanceVoyages(c);
  reconcileFleets(c);
}

// ── March ──────────────────────────────────────────────────────────────────

function doMarch(c: CampaignState): void {
  for (const a of c.armies) {
    /**
     * A host that fought on the road this month spends the month on that.
     *
     * `TurnPhase.Roads` runs before this one, so the month a road battle is
     * fought in is a month this loop would otherwise also charge to the march —
     * which is why the old code added a month to `moveT` here and took it
     * straight back off, netting nothing except a `moveTotal` that grew every
     * fight and a drawn counter that slid backwards down the road. Spending the
     * month on `regroup` instead says the same standstill once: the lane is
     * untouched, the host does not advance, and the following month it walks.
     */
    if (a.regroup > 0) {
      a.regroup--;
      continue;
    }
    if (a.moveTo < 0) continue;
    a.moveT--;
    if (a.moveT > 0) continue;
    landArmy(a, a.moveTo);
    a.stance = ArmyStance.Field;
    a.siegeTurns = 0;
    // A leg landed. If the order named somewhere further on, take the next one
    // this same turn, so a multi-province march costs its months and not an
    // extra turn of standing about at every border.
    continueMarch(c, a);
  }
  // A chase that was never resolved is over: it was an order for this turn.
  for (const a of c.armies) a.intercepting = -1;
  for (const a of c.armies) updateSupply(c, a);
}

/**
 * Fight the next meeting on the roads, if there is one.
 *
 * Same shape as `doNextContact`: one at a time, returning false when the roads
 * are quiet, because any one of these may be a battle the player fights by hand
 * and therefore may suspend the whole turn until a result comes back.
 */
function doNextRoadContact(w: World): boolean {
  const c = w.campaign;
  if (!c) return false;
  const contact = nextRoadContact(c);
  if (!contact) return false;

  logRoadContact(c, contact);
  openRoadBattle(c, roadSeed(c, contact.from, contact.to), contact);
  if (!c.pending) return true;
  if (!c.pending.playerFights) autoResolveBattle(w);
  return true;
}

/** A road battle's seed, like a province battle's: where and when, and nothing else. */
function roadSeed(c: CampaignState, from: number, to: number): number {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  return ((c.turn * 6151 + lo * 92821 + hi * 7717) ^ 0x2f6e2b1) >>> 0;
}

/**
 * Supply is a single number that only ever means one thing: an army sitting on
 * ground that is not its own is running down, and an army that runs out starts
 * losing men without anybody fighting it. It is the clock on every siege.
 */
function updateSupply(c: CampaignState, a: Army): void {
  /**
   * A host at sea eats the ship's stores and neither starves nor recovers.
   *
   * Its `at` points at the hull, not at ground it is standing on, so running
   * the ordinary test would read the supply state of whatever coast the fleet
   * happens to be off — which would have a host aboard a transport quietly
   * re-provisioning from an enemy province it is merely sailing past.
   */
  if (a.aboard >= 0) return;
  const prov = c.provinces[a.at];
  /**
   * A host standing on ground it has no right to be on is fed by nobody.
   *
   * The clause used to read "any owner you are not at war with", which was
   * right while the only two states were *at war* and *not allowed in* — but it
   * is the reason a peace never got anybody's armies off anybody's land. The
   * loser signs, the winner's hosts stop being at war with the ground they are
   * standing on, `friendly` flips to true, and they are provisioned to full
   * every month for the rest of the campaign by the country they just invaded.
   *
   * `isTrespassing` is false through the grace period, so an army caught out by
   * a peace has three months to walk out before anything happens to it. After
   * that the countryside closes and the existing hunger arithmetic — the same
   * one that ends sieges — does the rest. See `Army.trespassT`.
   */
  const friendly =
    prov.owner === a.owner ||
    (prov.owner >= 0 && !atWar(c, a.owner, prov.owner) && !isTrespassing(a));
  /**
   * A host inside walls somebody is sitting outside of eats the town's stores.
   *
   * This is the price that stops shelter being free. The ground under it is its
   * own, so the ordinary test says *friendly* and would provision it to full
   * every month for as long as the siege ran — which would make a besieged
   * capital with a host in it strictly better off than one without, and turn
   * "hide in the walls" into "become invulnerable". It starves slower than the
   * army outside, because it is behind its own granary, and it starves.
   */
  if (a.sheltered && prov.siegeBy >= 0 && prov.siegeBy !== a.owner) {
    a.supply = Math.max(0, a.supply - 0.14);
    if (a.supply <= 0) {
      const lost = applyLosses(a, 0.9);
      if (lost > 0) {
        logLine(c, `${a.name} starves behind the walls of ${provinceName(a.at)} — ${lost} gone.`);
      }
      if (a.regiments.length === 0) removeArmy(c, a.id);
    }
    return;
  }
  if (friendly) {
    a.supply = Math.min(1, a.supply + 0.5);
    return;
  }
  a.supply = Math.max(0, a.supply - (a.stance === ArmyStance.Besieging ? 0.28 : 0.2));
  if (a.supply <= 0) {
    const lost = applyLosses(a, 0.9);
    if (lost > 0) {
      logLine(c, `${a.name} loses ${lost} to hunger at ${provinceName(a.at)}.`);
    }
    if (a.regiments.length === 0) removeArmy(c, a.id);
  }
}

// ── Battles ────────────────────────────────────────────────────────────────

/**
 * Find and start the next fight, if there is one. Returns false when the board
 * has no unresolved contacts left, which is what ends the phase.
 *
 * Provinces are scanned in index order and armies in id order, so which battle
 * happens first is a fact about the board rather than about iteration luck.
 */
function doNextContact(w: World): boolean {
  const c = w.campaign;
  if (!c) return false;

  /**
   * Which provinces have somebody behind the gates — one pass, not six hundred.
   *
   * Asking `shelterDefender` per province would be a scan of every host on the
   * board for every province on it, inside a function the turn machine calls
   * once per contact until the board runs out of them. One walk of the army
   * list answers it for the whole map, and on a board where nobody has
   * sheltered anything the answer is an empty set and this costs nothing.
   */
  const walled = new Set<number>();
  for (const a of c.armies) {
    if (a.sheltered && a.aboard < 0 && a.moveTo < 0 && a.regiments.length > 0) walled.add(a.at);
  }

  for (let p = 0; p < c.provinces.length; p++) {
    /**
     * Hosts *in the open field* here — which is not the same list it was.
     *
     * A host inside the walls is not caught in the open, and that is the whole
     * of the owner's second item: *"army can either fitht open field or you can
     * choose army to hide in the walls"*. It is still on the board and it still
     * defends: `shelterDefender` hands it back below, when somebody actually
     * comes for the province it is in.
     */
    const here = armiesStandingIn(c, p).filter((a) => a.regiments.length > 0 && !a.sheltered);
    const prov = c.provinces[p];
    const hidden = walled.has(p) ? shelterDefender(c, p) : undefined;
    if (here.length === 0 && !hidden) continue;

    // Two hosts on the same ground, at war. The one that does not own the
    // province is the attacker; if neither owns it, the lower nation index is,
    // which is arbitrary and, far more importantly, stable.
    for (let i = 0; i < here.length; i++) {
      for (let j = i + 1; j < here.length; j++) {
        const a = here[i];
        const b = here[j];
        if (a.owner === b.owner) continue;
        if (!atWar(c, a.owner, b.owner)) continue;
        const aDefends = prov.owner === a.owner;
        const bDefends = prov.owner === b.owner;
        const attacker = aDefends ? b : bDefends ? a : a.owner <= b.owner ? a : b;
        const defender = attacker === a ? b : a;
        openBattle(c, battleSeed(c, p), p, attacker, defender);
        if (!c.pending) return true;
        if (!c.pending.playerFights) autoResolveBattle(w);
        return true;
      }
    }

    // One host, on somebody else's ground.
    for (const a of here) {
      if (prov.owner === a.owner) continue;
      if (!mayAttack(c, a.owner, p)) continue;

      // Nothing to fight and no wall to climb: the province simply changes hands.
      // Brigands squatting on it count as something to fight, so a camp blocks
      // the walk-in — otherwise unclaimed ground with a camp on it would be
      // taken by anyone who wandered past and the camp would survive the taking.
      // A host inside the walls is somebody home, so the walk-in is off.
      if (prov.garrison <= 5 && prov.fort === 0 && !hasCamp(c, p) && !hidden) {
        captureProvince(c, p, a.owner);
        logLine(c, `${nationName(c, a.owner)} walks into ${provinceName(p)}. Nobody was home.`);
        checkElimination(c);
        return true;
      }

      // A wall means a siege unless somebody has ordered the storm.
      if (prov.fort > 0 && a.stance !== ArmyStance.Assaulting) {
        if (a.stance !== ArmyStance.Besieging) {
          a.stance = ArmyStance.Besieging;
          a.siegeTurns = 0;
          prov.siegeBy = a.owner;
          prov.siegeT = 0;
          logLine(c, `${a.name} sits down in front of ${provinceName(p)}.`);
        }
        continue;
      }

      // The men in the walls are the defence. `openBattle` folds the rest of
      // what is inside in through `shelterSupport`, so two hosts behind one
      // gate are two hosts and not one.
      openBattle(c, battleSeed(c, p), p, a, hidden);
      if (!c.pending) return true;
      if (!c.pending.playerFights) autoResolveBattle(w);
      return true;
    }

    // Brigands. Last, because a war between nations outranks a policing problem
    // — and because a province that has just changed hands should settle who
    // holds it before anybody goes into the hills.
    const camp = campAt(c, p);
    if (camp) {
      for (const a of here) {
        if (a.stance === ArmyStance.Besieging) continue;
        openCampBattle(c, campSeed(c, p), p, a, camp);
        if (!c.pending) return true;
        if (!c.pending.playerFights) autoResolveBattle(w);
        return true;
      }
    }
  }
  return false;
}

/**
 * A battle's seed is a pure function of where and when it is fought, so the
 * tactical world a campaign hands you is reproducible from the campaign alone.
 */
function battleSeed(c: CampaignState, province: number): number {
  return ((c.turn * 7919 + province * 104729) ^ 0x5bf03635) >>> 0;
}

/** The same idea for a camp: a fight in the hills above a named province. */
function campSeed(c: CampaignState, province: number): number {
  return ((c.turn * 3467 + province * 15486047) ^ 0x1d7b93f) >>> 0;
}

// ── Sieges ─────────────────────────────────────────────────────────────────

function doSieges(c: CampaignState): void {
  for (const a of c.armies) {
    if (a.stance !== ArmyStance.Besieging) continue;
    const prov = c.provinces[a.at];
    if (prov.owner === a.owner) {
      a.stance = ArmyStance.Field;
      a.siegeTurns = 0;
      continue;
    }
    a.siegeTurns++;
    prov.siegeBy = a.owner;
    prov.siegeT = a.siegeTurns;
    // A besieged garrison does not regrow; it is being starved on purpose.
    prov.garrison = Math.max(0, prov.garrison - 12);

    if (a.siegeTurns >= Math.max(1, prov.fort) * SIEGE_TURNS_PER_FORT || prov.garrison <= 0) {
      logLine(
        c,
        `${provinceName(a.at)} opens its gates after ${a.siegeTurns} turns. ` +
          `Hunger did what ${nationName(c, a.owner)} could not.`,
      );
      captureProvince(c, a.at, a.owner);
      a.stance = ArmyStance.Field;
      a.siegeTurns = 0;
    }
  }
  // Clear stale siege marks on provinces nobody is sitting on any more.
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    if (prov.siegeBy < 0) continue;
    const still = armiesIn(c, p).some(
      (a) => a.owner === prov.siegeBy && a.stance === ArmyStance.Besieging,
    );
    if (!still) {
      prov.siegeBy = -1;
      prov.siegeT = 0;
    }
  }
  checkElimination(c);
}

// ── Income ─────────────────────────────────────────────────────────────────

function doIncome(c: CampaignState): void {
  // Provinces first: unrest settles and garrisons refill wherever nobody is
  // sitting outside the gate.
  // How many provinces each crown is trying to hold, counted once.
  // The lane adjacency is built once here and handed to every port, rather than
  // each port filtering the whole lane list for itself — the difference between
  // one walk of 260 lanes and 175 of them.
  const laneAdj = laneAdjacency(c);
  const holdings = c.nations.map(() => 0);
  for (const prov of c.provinces) if (prov.owner >= 0) holdings[prov.owner]++;

  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    /**
     * An overextended power cannot pacify what it takes.
     *
     * Income was linear in provinces and nothing cost more for holding many, so
     * a power that got ahead got further ahead: more ground, more gold, more
     * hosts, more ground. The realm averaged a healthy nine provinces to the
     * leader while handing one crown twenty of twenty-four in a fifth of its
     * campaigns — a mean that hid the only case anybody complains about.
     *
     * Unrest is the brake, because it is the one already here: fresh conquests
     * sit restive, and a sprawling empire settles them slower. Up to a core it
     * costs nothing; past that every province drags on the pacification of all
     * the others. It does not stop conquest — it makes the twentieth province
     * worth less than the sixth, which is the shape the snowball needed.
     */
    const held = prov.owner >= 0 ? holdings[prov.owner] : 0;
    const strain = 1 / (1 + Math.max(0, held - UNREST_CORE) * UNREST_STRAIN);
    /**
     * A market town has something to lose and settles faster for it — the fifth
     * rung of the ladder, and the one place unrest is not purely a function of
     * how much ground a crown has bitten off.
     *
     * …and the temple, which is the one place it is not a function of the ground
     * at all. A confession that has been kept sweet preaches patience in a bad
     * year and a confession that has been slighted preaches something else; the
     * multiplier runs half to one-and-a-half. This is the Temple's whole lever
     * and it is the only thing that reads its standing.
     */
    const faith = prov.owner >= 0 ? estateUnrestScale(c, prov.owner) : 1;
    prov.unrest = Math.max(
      0,
      prov.unrest - UNREST_DECAY * strain * tierUnrestRelief(prov.tier) * faith,
    );
    if (prov.owner >= 0 && prov.siegeBy < 0) {
      // A camp in the hills does not stop recruitment, it eats into it. Halving
      // the regrowth is what makes a province with brigands on it *stay* soft,
      // which is what turns ignoring one into a decision rather than a delay.
      const regrowth = hasCamp(c, p) ? GARRISON_REGROWTH * 0.5 : GARRISON_REGROWTH;
      // A militia mends itself. The third rung — and the great families, who are
      // the people who actually turn out for a watch-list. Their whole lever.
      prov.garrison = Math.min(
        garrisonCap(c, p),
        prov.garrison +
          regrowth * tierRegrowth(prov.tier) * estateGarrisonScale(c, prov.owner),
      );
      /**
       * The floor, and the only place it is applied.
       *
       * *"capital and main cities not villages should always have some small
       * garrison"* — always, which means it cannot be trickled back at twenty-two
       * points a month after a bad battle. A seat of a crown that was stormed
       * last month has its watch back on the walls this month, because the watch
       * is a property of the place and not of the fight.
       *
       * It is here, inside the `siegeBy < 0` branch, on purpose and that is the
       * load-bearing detail: **a siege still empties a holding.** An army that
       * sits down in front of a capital grinds the garrison down by twelve a
       * month exactly as it always did, and nothing puts it back while the tents
       * are up. What has changed is only that an attacker cannot find a great
       * city standing empty because somebody else beat it up a year ago.
       */
      prov.garrison = Math.max(prov.garrison, standingGarrison(c, p));
    }
  }

  for (let n = 0; n < c.nations.length; n++) {
    const nation = c.nations[n];
    if (!nation.alive) continue;

    /**
     * Where a crown's goods actually go.
     *
     * Coin and recruits are national — they are added to the treasury and the
     * manpower pool exactly as they always were. Materials are not: they are
     * carted to the nearest city this crown can reach over its own roads, and
     * they stay there until somebody builds something out of them. The supply
     * map is computed once for the whole crown rather than once per province,
     * which is the entire reason it is a multi-source search.
     */
    const supply = supplyMap(c, n);

    let gold = 0;
    let levy = 0;
    for (let p = 0; p < c.provinces.length; p++) {
      const prov = c.provinces[p];
      if (prov.owner !== n) continue;
      /**
       * The tax half, the levies and the goods, all from the one reader.
       *
       * `holdingIncome.gold` is already net of `TRADE_SHARE` — the other third
       * of a province's wealth is trade and comes back only through control of
       * its node. It is applied inside `holdingIncome` rather than here so that
       * the world map's panel and this loop cannot disagree about what a
       * province pays, which is the failure `provinceTradeValue`'s own comment
       * warns about.
       */
      const income = holdingIncome(c, p);
      gold += income.gold;
      levy += income.levy;
      /**
       * The harbour's cut, and it is *not* scaled by unrest or development.
       *
       * A port earns on traffic that is passing through rather than on what the
       * province grows, so a restive newly-taken city with a good harbour still
       * makes money on the wharf — which is exactly why a coastal conquest is
       * worth making. The siege case is already handled inside `portIncome`,
       * where it belongs: a blockaded harbour trades with nobody.
       *
       * Added *outside* `holdingIncome` on purpose. That reader is the tax half
       * of what the ground itself yields, and the tier ladder, the trade split
       * and the world-map panel all read it; a harbour fee folded into it would
       * be taxed as though it were grain and would silently hand a third of
       * every wharf to whoever controls the trade node.
       */
      gold += portIncome(c, p, laneAdj);
      // The province's strategic good and its tithe in kind. Both go to the
      // yard of the city that supplies it; coin and souls stay national.
      for (const g of income.goods) depositGoods(c, supply, n, p, g.key, g.amount);
    }
    /**
     * …and then the realm eats.
     *
     * After the tithe is carted and before the wages are paid, which is the
     * order the sentence reads in: collect, eat, pay. This month's harvest
     * feeds this month, so a crown that has just taken a grain province is fed
     * by it immediately rather than a month late.
     *
     * The bill is scaled by the ground held and the hosts standing on it, comes
     * out of the city yards first and the national stores after, and what
     * cannot be paid comes back as unrest. The whole argument is at the top of
     * `food.ts`; this is the one line it asks of this file.
     */
    eatFood(c, n);
    /**
     * …and then the two estates who decide how much of it the crown actually
     * sees.
     *
     * The counting houses take the tithe in and hand it over, or do not: the
     * multiplier runs 0.6 to 1.4 on everything the ground and the wharf pay. The
     * hosts are the countryside sending men, and theirs runs 0.5 to 1.5 — except
     * below `HOSTS_STRIKE`, where it is **zero**, and the muster simply fails.
     *
     * That zero is the one discontinuity in the estates and it is the point of
     * them: the brief asked for a refusal at the worst moment, and a levy that
     * merely shrinks is a tax rather than a refusal. It cannot deadlock a crown —
     * the manpower already banked is untouched, so a host can still be raised out
     * of the bank, and raising one is the largest single term pushing the Hosts
     * back up. See `estates.ts`.
     */
    nation.treasury.gold += gold * estateGoldScale(c, n);
    nation.manpower += levy * estateLevyScale(c, n);

    // Wages. An army is a running cost on the map exactly as it is in a
    // settlement, and for the same reason: it stops the answer from being
    // "raise everything and never disband". Hulls cost the same way and for the
    // same reason — a fleet left rotting at anchor between wars is a decision.
    let upkeep = 0;
    for (const a of c.armies) if (a.owner === n) upkeep += rawPoints(a) * UPKEEP_GOLD_PER_POINT;
    upkeep += fleetUpkeep(c, n) * SHIP_UPKEEP_GOLD;
    nation.treasury.gold -= upkeep;
    if (nation.treasury.gold < 0) {
      // The purse is empty and the men notice within the turn.
      nation.treasury.gold = 0;
      for (const a of c.armies) {
        if (a.owner !== n) continue;
        a.supply = Math.max(0, a.supply - 0.35);
      }
      // Every human crown hears about its own empty treasury, and the line
      // must be decided the same way on every client.
      if (c.humanNations.includes(n)) logLine(c, 'The treasury is empty. The hosts have noticed.');
    }
    for (const k of RESOURCE_KEYS) {
      if (nation.treasury[k] < 0) nation.treasury[k] = 0;
    }
    nation.manpower = Math.max(0, nation.manpower);
  }
}

// ── Small shared readers the UI wants ──────────────────────────────────────

/**
 * What a nation will collect next month, net of wages, for the world-map panel.
 *
 * Materials are in it now, and they are reported as a *realm* total rather than
 * per yard: the panel that uses this is answering "what is my crown worth a
 * month", and the answer to that is not divided by where the timber happens to
 * be sitting. `holdingReport` is the one that knows about yards — see
 * `holdings.ts`.
 */
export function projectedIncome(
  c: CampaignState,
  nation: number,
): { gold: number; levy: number; goods: Array<{ key: ResourceKey; amount: number }> } {
  let gold = 0;
  let levy = 0;
  const goods: Array<{ key: ResourceKey; amount: number }> = [];
  const laneAdj = laneAdjacency(c);
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    const income = holdingIncome(c, p);
    gold += income.gold;
    levy += income.levy;
    // The wharf, on the same terms `doIncome` pays it — the forecast and the
    // month have to agree about a harbour as much as about a field.
    gold += portIncome(c, p, laneAdj);
    for (const g of income.goods) {
      const hit = goods.find((x) => x.key === g.key);
      if (hit) hit.amount += g.amount;
      else goods.push({ key: g.key, amount: g.amount });
    }
  }
  /**
   * The estates' cut, on exactly the terms `doIncome` takes it — which means
   * **before** the markets are added and not after.
   *
   * The counting houses take a cut of the *tithe*: the ground and the wharf, the
   * two things `doIncome` accumulates into `gold` before it touches the treasury.
   * What the markets pay is settled somewhere else entirely — `runFlow` pays it
   * straight into the treasuries during `tradeTurn` — and it already has its own
   * mechanism for who gets what, which is control of the node. Scaling it here
   * would both double-count the merchants and make this forecast disagree with
   * the month, which is the exact failure `holdingIncome`'s own comment warns
   * about: a panel promising a hundred and forty gold from a crown whose counting
   * houses will only hand over a hundred is a panel that has taught the player to
   * distrust it.
   *
   * The wages below are not scaled either. Nobody's estate gets a say in what an
   * army costs to keep.
   */
  gold *= estateGoldScale(c, nation);
  levy *= estateLevyScale(c, nation);
  // Plus what the markets paid last month. Last month's rather than a forecast,
  // because the honest forecast requires resolving the whole graph and the
  // difference between the two is a rounding error next to a war.
  gold += tradeIncomeOf(c, nation);
  for (const a of c.armies) {
    if (a.owner === nation) gold -= rawPoints(a) * UPKEEP_GOLD_PER_POINT;
  }
  // Hulls are a running cost like men, so the forecast has to net them off too
  // or a realm with a fleet reads richer on the panel than it is in the ledger.
  gold -= fleetUpkeep(c, nation) * SHIP_UPKEEP_GOLD;
  return { gold, levy, goods };
}

/**
 * What a nation's harbours alone bring in, for the world map's ledger line.
 *
 * Separate from `projectedIncome` because it is the number that answers "was
 * the port worth building", and burying it inside a total answers nothing.
 */
export function projectedPortIncome(c: CampaignState, nation: number): number {
  const laneAdj = laneAdjacency(c);
  let gold = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    gold += portIncome(c, p, laneAdj);
  }
  return gold;
}
